import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    AlertTriangle,
    CheckCircle,
    Clock3,
    ListChecks,
    Route,
    Search,
    Send,
    X,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
    approveCampaignBlueprint,
    checkCampaignStatusGuard,
    clearApproveState,
    clearBlueprintPreview,
    clearStatusGuardState,
    fetchOutreachCapStatus,
    previewCampaignBlueprint,
} from "../store/outreachSlice";
import {
    clearCreateProductState,
    clearSelectedProduct,
    createProduct,
    fetchProductById,
    fetchProducts,
    setSelectedProduct,
} from "../store/productsSlice";
import ProductSelectableList from "./products/ProductSelectableList";
import { ROUTES } from "../config/constants";
import {
    getFallbackEmailValidationMessage,
    getMinimumLettersMessage,
    getPlainTextFromHtml,
    hasMinimumLetters,
    plainTextToSimpleHtml,
} from "../utils/textValidation";

const TOTAL_STEPS = 3;
const DEFAULT_TONE_SETTING =
    "Professional, highly consultative, no sales buzzwords";

const getProductId = (product) =>
    product?.id || product?.product_id || product?.productId || "";

const getStepTextValue = (value) =>
    value === undefined || value === null ? "" : String(value);

const isEmailStep = (step = {}) =>
    getStepTextValue(step.channel_type).trim().toLowerCase() === "email";

const isProductPanelOverlayScreen = () =>
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 1023px)").matches;

const formatCapValue = (value) => {
    const numberValue = Number(value);

    return Number.isFinite(numberValue) ? numberValue.toLocaleString() : "--";
};

const formatPlanName = (tier) => {
    if (!tier) return "current";

    const normalizedTier = String(tier)
        .trim()
        .replace(/[_-]+/g, " ");

    return normalizedTier.charAt(0).toUpperCase() + normalizedTier.slice(1);
};

const isBlueprintIncompleteError = (error) =>
    error?.type === "blueprint_incomplete" ||
    error?.error_code === "BLUEPRINT_INCOMPLETE";

const getModalErrorMessage = (error) =>
    typeof error === "string" ? error : error?.message || "";

const getApproveSuccessToast = (response) => {
    const status = String(response?.status || "")
        .trim()
        .toLowerCase();

    if (status === "success") {
        return {
            type: "success",
            message: "Campaign launched successfully.",
        };
    }

    if (status === "held") {
        return {
            type: "warning",
            message:
                "Campaign created successfully, but it is currently paused due to an infrastructure issue.",
        };
    }

    if (status === "cohort_empty") {
        return {
            type: "warning",
            message:
                "Campaign was created, but no eligible leads were available to enroll.",
        };
    }

    return {
        type: "success",
        message: "Campaign created successfully.",
    };
};

const normalizeDelayDays = (value, index = 0) => {
    const numericValue = Number(value);
    const minimumDelay = index === 0 ? 0 : 1;

    return Number.isFinite(numericValue)
        ? Math.max(minimumDelay, numericValue)
        : minimumDelay;
};

const getNormalizedDesignedSteps = (preview) =>
    Array.isArray(preview?.designed_steps)
        ? preview.designed_steps.map((step, index) => ({
              ...step,
              delay_days_from_previous: normalizeDelayDays(
                  step?.delay_days_from_previous,
                  index,
              ),
          }))
        : [];

const defaultCampaignDesignLoadingTexts = [
    "OptiMind's AI is analyzing your campaign details.",
    "OptiMind's AI is planning the best outreach approach.",
    "OptiMind's AI is designing your campaign steps.",
    "OptiMind's AI is refining the messaging for each step.",
    "OptiMind's AI is reviewing timing and sequence flow.",
    "OptiMind's AI is checking the campaign for consistency.",
];

const getCampaignDesignLoadingTexts = () => {
    const envMessages = process.env.REACT_APP_CAMPAIGN_DESIGN_LOADING_MESSAGES;

    if (!envMessages) return defaultCampaignDesignLoadingTexts;

    const messages = envMessages
        .split("|")
        .map((message) => message.trim())
        .filter(Boolean);

    return messages.length > 0 ? messages : defaultCampaignDesignLoadingTexts;
};

const campaignDesignLoadingIcons = [
    Search,
    Route,
    ListChecks,
    Send,
    Clock3,
    CheckCircle,
];

const CAMPAIGN_DESIGN_LOADING_MESSAGES = [
    ...getCampaignDesignLoadingTexts().map((text, index) => ({
        text,
        icon:
            campaignDesignLoadingIcons[index] ||
            campaignDesignLoadingIcons[campaignDesignLoadingIcons.length - 1],
    })),
];

const renderCampaignDesignMessage = (text) => {
    const highlightText = "OptiMind's AI";
    const parts = String(text || "").split(highlightText);

    if (parts.length === 1) return text;

    return parts.map((part, index) => (
        <React.Fragment key={`${part}-${index}`}>
            {part}
            {index < parts.length - 1 && (
                <span className="text-indigo-700">{highlightText}</span>
            )}
        </React.Fragment>
    ));
};

const CampaignDesignLoader = ({
    isLoading,
    messages = CAMPAIGN_DESIGN_LOADING_MESSAGES,
    intervalMs = 5000,
}) => {
    const [messageIndex, setMessageIndex] = useState(0);
    const messageCount = Math.max(messages.length, 1);

    useEffect(() => {
        if (!isLoading) {
            setMessageIndex(0);
            return undefined;
        }

        setMessageIndex(0);

        const timer = window.setInterval(() => {
            setMessageIndex((currentIndex) =>
                (currentIndex + 1) % messageCount,
            );
        }, intervalMs);

        return () => window.clearInterval(timer);
    }, [intervalMs, isLoading, messageCount]);

    if (!isLoading) return null;

    const currentMessage = messages[messageIndex];
    const LoadingIcon = currentMessage?.icon || CheckCircle;

    return (
        <div className="app-fade-in absolute inset-0 z-30 flex items-center justify-center bg-white/80 px-4 backdrop-blur-sm">
            <div
                role="status"
                aria-live="polite"
                className="w-full max-w-sm px-5 py-6 text-center"
            >
                <div className="flex items-end justify-center gap-1.5">
                    {[0, 1, 2].map((dot) => (
                        <span
                            key={dot}
                            className="h-2.5 w-2.5 animate-bounce rounded-full bg-indigo-600"
                            style={{ animationDelay: `${dot * 160}ms` }}
                        />
                    ))}
                </div>
                <div
                    key={messageIndex}
                    className="app-sweep-up mt-4 flex items-center justify-center gap-2 whitespace-nowrap text-sm font-semibold text-slate-900"
                >
                    <LoadingIcon
                        size={18}
                        className="shrink-0 text-indigo-600"
                        aria-hidden="true"
                    />
                    <span>
                        {renderCampaignDesignMessage(currentMessage?.text)}
                    </span>
                </div>
                <p className="mt-2 text-xs leading-5 text-slate-500">
                    This may take a few seconds to a couple of minutes.
                </p>
            </div>
        </div>
    );
};

const CampaignBlueprintModal = ({
    isOpen,
    onClose,
    activeSearchId,
    organizationId,
    leadIds = [],
    startAtDesignStep = false,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
        selectedProduct,
        createProductStatus,
        createProductError,
        products,
        productsStatus,
        productsError,
    } = useSelector((state) => state.products);

    const {
        previewStatus,
        previewError,
        approveStatus,
        approveError,
        capStatus,
        capStatusStatus,
        capStatusError,
        statusGuardStatus,
    } = useSelector((state) => state.outreach);

    const [currentStep, setCurrentStep] = useState(1);
    const [productName, setProductName] = useState("");
    const [productDescription, setProductDescription] = useState("");
    const [campaignName, setCampaignName] = useState("");
    const [campaignObjective, setCampaignObjective] = useState("");
    const [targetAudience, setTargetAudience] = useState("");
    const [productValueProposition, setProductValueProposition] =
        useState("");
    const [toneSetting, setToneSetting] = useState(DEFAULT_TONE_SETTING);
    const [timelineSteps, setTimelineSteps] = useState([]);
    const [formError, setFormError] = useState("");
    const [isProductPanelOpen, setIsProductPanelOpen] = useState(false);
    const [selectedFlowProductId, setSelectedFlowProductId] = useState("");
    const [selectedProductSource, setSelectedProductSource] = useState("");
    const requestInFlight = useRef(false);
    const modalBodyRef = useRef(null);
    const isShortCampaignFlow = startAtDesignStep;
    const displayTotalSteps = isShortCampaignFlow ? 2 : TOTAL_STEPS;
    const displayStep = isShortCampaignFlow
        ? Math.max(1, currentStep - 1)
        : currentStep;
    const productList = Array.isArray(products) ? products : [];
    const shouldShowProductPanel = currentStep === 1 && isProductPanelOpen;
    const selectedProductId = getProductId(selectedProduct);
    const hasSelectedProductForFlow = Boolean(
        selectedFlowProductId && selectedProductId === selectedFlowProductId,
    );
    const capRemaining = Number(capStatus?.remaining);
    const hasReachedCampaignCap =
        capStatusStatus === "succeeded" &&
        Number.isFinite(capRemaining) &&
        capRemaining <= 0;
    const isCheckingCampaignCap =
        currentStep === 3 &&
        (capStatusStatus === "idle" || capStatusStatus === "loading");
    const isCheckingStatusGuard = statusGuardStatus === "loading";
    const campaignCapWarning = hasReachedCampaignCap
        ? `You have reached your active campaign limit for the ${formatPlanName(
              capStatus?.tier,
          )} plan. Pause or complete an existing campaign, or upgrade your plan to launch more campaigns.`
        : "";
    const selectedProductBadgeText =
        selectedProductSource === "existing"
            ? "Selected existing product"
            : "Selected product";
    const canCreateNewProduct =
        hasMinimumLetters(productName) && hasMinimumLetters(productDescription);
    const canDesignCampaign =
        hasMinimumLetters(campaignName) &&
        hasMinimumLetters(campaignObjective) &&
        hasMinimumLetters(targetAudience) &&
        hasMinimumLetters(productValueProposition) &&
        hasMinimumLetters(toneSetting);
    const hasValidTimelineStepText =
        timelineSteps.length > 0 &&
        timelineSteps.every((step) =>
            hasMinimumLetters(step?.step_objective),
        );

    const isBusy =
        createProductStatus === "loading" ||
        isCheckingStatusGuard ||
        previewStatus === "loading" ||
        approveStatus === "loading";

    const resetWorkflow = useCallback((options = {}) => {
        const {
            preserveProduct = false,
            nextStep = 1,
            openProductPanel = false,
        } = options;

        setCurrentStep(nextStep);
        setProductName("");
        setProductDescription("");
        setCampaignName("");
        setCampaignObjective("");
        setTargetAudience("");
        setProductValueProposition("");
        setToneSetting(DEFAULT_TONE_SETTING);
        setTimelineSteps([]);
        setFormError("");
        setIsProductPanelOpen(openProductPanel);
        setSelectedFlowProductId("");
        setSelectedProductSource("");
        if (!preserveProduct) {
            dispatch(clearSelectedProduct());
        }
        dispatch(clearCreateProductState());
        dispatch(clearBlueprintPreview());
        dispatch(clearApproveState());
        dispatch(clearStatusGuardState());
        requestInFlight.current = false;
    }, [dispatch]);

    useEffect(() => {
        if (!isOpen) return;

        const shouldOpenProductPanel = !startAtDesignStep;

        resetWorkflow({
            preserveProduct: startAtDesignStep,
            nextStep: startAtDesignStep ? 2 : 1,
            openProductPanel: shouldOpenProductPanel,
        });
    }, [isOpen, resetWorkflow, startAtDesignStep]);

    useEffect(() => {
        if (!isOpen || startAtDesignStep || currentStep !== 1) return;
        if (!isProductPanelOpen) return;

        dispatch(fetchProducts({ organizationId }));
    }, [
        currentStep,
        dispatch,
        isOpen,
        isProductPanelOpen,
        organizationId,
        startAtDesignStep,
    ]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const handleEscape = (event) => {
            if (event.key === "Escape" && !isBusy) {
                resetWorkflow({ preserveProduct: startAtDesignStep });
                onClose();
            }
        };

        window.addEventListener("keydown", handleEscape);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", handleEscape);
        };
    }, [isOpen, isBusy, onClose, resetWorkflow, startAtDesignStep]);

    useEffect(() => {
        if (!isOpen || currentStep !== 3) return;
        if (capStatusStatus !== "idle") return;

        dispatch(fetchOutreachCapStatus());
    }, [capStatusStatus, currentStep, dispatch, isOpen]);

    useEffect(() => {
        if (!isOpen || !selectedFlowProductId) return;
        if (getProductId(selectedProduct) !== selectedFlowProductId) {
            return;
        }

        setProductName(selectedProduct?.name || "");
        setProductDescription(selectedProduct?.description || "");
    }, [isOpen, selectedFlowProductId, selectedProduct]);

    const handleClose = () => {
        if (isBusy) return;

        resetWorkflow({ preserveProduct: startAtDesignStep });
        onClose();
    };

    const verifyCampaignAvailability = async (productId) => {
        const normalizedSearchId = String(activeSearchId || "").trim();
        const normalizedProductId = String(productId || "").trim();

        if (!normalizedSearchId || !normalizedProductId) {
            toast.error(
                "Unable to verify campaign availability right now.",
            );
            return false;
        }

        dispatch(clearStatusGuardState());

        try {
            const statusGuard = await dispatch(
                checkCampaignStatusGuard({
                    searchId: normalizedSearchId,
                    productId: normalizedProductId,
                }),
            ).unwrap();

            if (statusGuard?.can_start === true) {
                return true;
            }

            if (statusGuard?.can_start === false) {
                toast.error(
                    "A campaign is already running for this product and search combination.",
                );
                return false;
            }

            toast.error(
                "Unable to verify campaign availability right now.",
            );
            return false;
        } catch {
            toast.error(
                "Unable to verify campaign availability right now.",
            );
            return false;
        } finally {
            dispatch(clearStatusGuardState());
        }
    };

    const handleCreateProduct = async (event) => {
        event.preventDefault();

        if (requestInFlight.current) return;

        const name = productName.trim();
        const description = productDescription.trim();

        if (hasSelectedProductForFlow) {
            setFormError(
                "Clear the selected product before creating a new one.",
            );
            return;
        }

        if (!name || !description) {
            setFormError("Product name and product description are required.");
            return;
        }

        if (!hasMinimumLetters(name)) {
            setFormError(getMinimumLettersMessage("Product name"));
            return;
        }

        if (!hasMinimumLetters(description)) {
            setFormError(getMinimumLettersMessage("Product description"));
            return;
        }

        setFormError("");
        requestInFlight.current = true;

        try {
            const createdProduct = await dispatch(
                createProduct({
                    name,
                    description,
                    organizationId,
                }),
            ).unwrap();
            setSelectedFlowProductId(getProductId(createdProduct));
            setSelectedProductSource("created");
            setProductName(name);
            setProductDescription(description);
            const canStartCampaign = await verifyCampaignAvailability(
                getProductId(createdProduct),
            );

            if (!canStartCampaign) return;

            setCurrentStep(2);
        } catch {
            // The thunk error is rendered from Redux.
        } finally {
            requestInFlight.current = false;
        }
    };

    const handleRetryProducts = () => {
        dispatch(fetchProducts({ organizationId }));
    };

    const handleSelectExistingProduct = (product) => {
        if (isBusy) return;

        const productId = getProductId(product);

        if (!productId) {
            setFormError("This product is missing its product id.");
            return;
        }

        setFormError("");
        dispatch(clearStatusGuardState());
        dispatch(clearCreateProductState());
        setSelectedFlowProductId(productId);
        setSelectedProductSource("existing");
        setProductName(product?.name || "");
        setProductDescription(product?.description || "");
        dispatch(setSelectedProduct(product));
        dispatch(fetchProductById(productId));

        if (isProductPanelOverlayScreen()) {
            setIsProductPanelOpen(false);
        }
    };

    const handleGoToNextStep = async () => {
        if (isBusy || requestInFlight.current) return;

        if (!hasSelectedProductForFlow) {
            setFormError("Choose an existing product before continuing.");
            setIsProductPanelOpen(true);
            return;
        }

        setFormError("");
        requestInFlight.current = true;

        try {
            const canStartCampaign = await verifyCampaignAvailability(
                selectedProductId,
            );

            if (!canStartCampaign) return;

            setIsProductPanelOpen(false);
            setCurrentStep(2);
        } finally {
            requestInFlight.current = false;
        }
    };

    const handleClearProduct = () => {
        if (isBusy) return;

        setFormError("");
        setProductName("");
        setProductDescription("");
        setSelectedFlowProductId("");
        setSelectedProductSource("");
        dispatch(clearSelectedProduct());
        dispatch(clearCreateProductState());
        dispatch(clearStatusGuardState());
    };

    const handleDesignCampaign = async (event) => {
        event.preventDefault();

        if (requestInFlight.current) return;

        const name = campaignName.trim();
        const objective = campaignObjective.trim();
        const audience = targetAudience.trim();
        const valueProposition = productValueProposition.trim();
        const tone = toneSetting.trim();

        // checks if user created product before designing campaign
        if (!selectedProduct?.id) {
            setFormError("Create a product before designing the campaign.");
            return;
        }

        if (!name) {
            setFormError("Please enter a campaign name.");
            return;
        }

        if (!hasMinimumLetters(name)) {
            setFormError(getMinimumLettersMessage("Campaign name"));
            return;
        }

        if (!objective || !audience || !valueProposition || !tone) {
            setFormError(
                "Campaign objective, target audience, product value proposition, and tone setting are required.",
            );
            return;
        }

        if (!hasMinimumLetters(objective)) {
            setFormError(getMinimumLettersMessage("Campaign objective"));
            return;
        }

        if (!hasMinimumLetters(audience)) {
            setFormError(getMinimumLettersMessage("Target audience"));
            return;
        }

        if (!hasMinimumLetters(valueProposition)) {
            setFormError(
                getMinimumLettersMessage("Product value proposition"),
            );
            return;
        }

        if (!hasMinimumLetters(tone)) {
            setFormError(getMinimumLettersMessage("Tone setting"));
            return;
        }

        setFormError("");
        requestInFlight.current = true;

        try {
            const preview = await dispatch(
                previewCampaignBlueprint({
                    product_id: selectedProduct.id,
                    campaign_objective: objective,
                    target_audience: audience,
                    campaign_context: {
                        campaign_objective: objective,
                        product_value_proposition: valueProposition,
                        tone_setting: tone,
                    },
                }),
            ).unwrap();

            setCampaignName(name);
            setCampaignObjective(objective);
            setTargetAudience(audience);
            setProductValueProposition(valueProposition);
            setToneSetting(tone);
            // all the designed steps are being stored in the "timelineSteps"
            setTimelineSteps(getNormalizedDesignedSteps(preview));
            dispatch(fetchOutreachCapStatus());
            setCurrentStep(3);
        } catch {
            // The thunk error is rendered from Redux.
        } finally {
            requestInFlight.current = false;
        }
    };

    const handleTimelineChange = (index, field, value) => {
        setFormError("");
        setTimelineSteps((currentSteps) =>
            currentSteps.map((step, stepIndex) => {
                if (stepIndex !== index) return step;

                return {
                    ...step,
                    [field]:
                        field === "delay_days_from_previous"
                            ? normalizeDelayDays(value, stepIndex)
                            : value,
                };
            }),
        );
    };

    const handleRegenerateBlueprintRecovery = async () => {
        if (isBusy || requestInFlight.current) return;

        const objective = campaignObjective.trim();
        const audience = targetAudience.trim();
        const valueProposition = productValueProposition.trim();
        const tone = toneSetting.trim();

        if (!selectedProduct?.id) {
            setFormError("Create a product before designing the campaign.");
            return;
        }

        if (!objective || !audience || !valueProposition || !tone) {
            setFormError(
                "Campaign objective, target audience, product value proposition, and tone setting are required.",
            );
            return;
        }

        if (!hasMinimumLetters(objective)) {
            setFormError(getMinimumLettersMessage("Campaign objective"));
            return;
        }

        if (!hasMinimumLetters(audience)) {
            setFormError(getMinimumLettersMessage("Target audience"));
            return;
        }

        if (!hasMinimumLetters(valueProposition)) {
            setFormError(
                getMinimumLettersMessage("Product value proposition"),
            );
            return;
        }

        if (!hasMinimumLetters(tone)) {
            setFormError(getMinimumLettersMessage("Tone setting"));
            return;
        }

        setFormError("");
        requestInFlight.current = true;

        try {
            const preview = await dispatch(
                previewCampaignBlueprint({
                    product_id: selectedProduct.id,
                    campaign_objective: objective,
                    target_audience: audience,
                    campaign_context: {
                        campaign_objective: objective,
                        product_value_proposition: valueProposition,
                        tone_setting: tone,
                    },
                }),
            ).unwrap();

            dispatch(clearApproveState());
            setCampaignObjective(objective);
            setTargetAudience(audience);
            setProductValueProposition(valueProposition);
            setToneSetting(tone);
            setTimelineSteps(getNormalizedDesignedSteps(preview));
            dispatch(fetchOutreachCapStatus());
            setCurrentStep(3);
        } catch {
            // The thunk error is rendered from Redux.
        } finally {
            requestInFlight.current = false;
        }
    };

    const handleEditBlueprintManually = () => {
        if (isBusy) return;

        setFormError("");
        dispatch(clearApproveState());
        window.requestAnimationFrame(() => {
            document.getElementById("campaign-step-objective-0")?.focus();
        });
    };

    const handleApproveCampaign = async () => {
        if (requestInFlight.current) return;

        const name = campaignName.trim();
        const objective = campaignObjective.trim();
        const audience = targetAudience.trim();
        const valueProposition = productValueProposition.trim();
        const tone = toneSetting.trim();

        if (!name) {
            setFormError("Please enter a campaign name.");
            return;
        }

        if (!hasMinimumLetters(name)) {
            setFormError(getMinimumLettersMessage("Campaign name"));
            return;
        }

        if (!objective || !audience || !valueProposition || !tone) {
            setFormError(
                "Campaign objective, target audience, product value proposition, and tone setting are required.",
            );
            return;
        }

        if (!selectedProduct?.id) {
            setFormError(
                "The selected product is missing. Go back and create it again.",
            );
            return;
        }
        // checks if the actie search is present or not
        if (!activeSearchId) {
            setFormError(
                "Please select or run a search before starting a campaign.",
            );
            return;
        }

        // checks if the orginanization is present or not
        if (!organizationId) {
            setFormError(
                "Please set organization context before starting a campaign.",
            );
            return;
        }

        // only gets the unique and valid id's, removes id that are null, undefined, empty
        const uniqueLeadIds = [
            ...new Set(
                (Array.isArray(leadIds) ? leadIds : []).filter(Boolean),
            ),
        ];

        if (uniqueLeadIds.length === 0) {
            setFormError("No lead IDs are available for this campaign.");
            return;
        }

        if (timelineSteps.length === 0) {
            setFormError(
                "No campaign steps were generated. Go back and design the campaign again.",
            );
            return;
        }

        if (!hasValidTimelineStepText) {
            setFormError(
                "Every campaign step objective must contain at least 3 letters.",
            );
            return;
        }

        if (hasReachedCampaignCap) {
            setFormError(campaignCapWarning);
            return;
        }

        // validates every field in timeline before user approves campaign design
        const hasInvalidStep = timelineSteps.some(
            (step, index) =>
                step?.step_number === undefined ||
                step?.step_number === null ||
                !step?.channel_type ||
                !step?.step_objective?.trim() ||
                !hasMinimumLetters(step?.step_objective) ||
                !Number.isFinite(Number(step?.delay_days_from_previous)) ||
                !Number.isInteger(Number(step.delay_days_from_previous)) ||
                Number(step.delay_days_from_previous) < (index === 0 ? 0 : 1),
        );

        if (hasInvalidStep) {
            setFormError(
                "Every campaign step needs a step number, channel, objective, and a valid whole-number delay. Only the first step can start at 0 days.",
            );
            return;
        }

        const invalidFallbackEmailMessage = timelineSteps.reduce(
            (message, step) => {
                if (message || !isEmailStep(step)) return message;

                return getFallbackEmailValidationMessage({
                    subject: step.subject,
                    html_body: step.html_body,
                });
            },
            "",
        );

        if (invalidFallbackEmailMessage) {
            setFormError(invalidFallbackEmailMessage);
            return;
        }

        const approvedSteps = timelineSteps.map((step) => ({
            step_number: step.step_number,
            delay_days_from_previous: Number(
                step.delay_days_from_previous,
            ),
            channel_type: step.channel_type,
            step_objective: step.step_objective.trim(),
            subject: getStepTextValue(step.subject).trim(),
            html_body: getStepTextValue(step.html_body).trim(),
        }));

        setFormError("");
        requestInFlight.current = true;

        try {
            const approveResponse = await dispatch(
                approveCampaignBlueprint({
                    organization_id: organizationId,
                    product_id: selectedProduct.id,
                    search_id: activeSearchId,
                    sequence_name: name,
                    campaign_objective: objective,
                    target_audience: audience,
                    campaign_context: {
                        campaign_objective: objective,
                        product_value_proposition: valueProposition,
                        tone_setting: tone,
                    },
                    lead_ids: uniqueLeadIds,
                    approved_steps: approvedSteps,
                }),
            ).unwrap();

            const successToast = getApproveSuccessToast(approveResponse);

            if (successToast.type === "warning") {
                toast.warning(successToast.message);
            } else {
                toast.success(successToast.message);
            }

            resetWorkflow({ preserveProduct: startAtDesignStep });
            onClose();
            navigate(ROUTES.CAMPAIGNS);
        } catch {
            // The thunk error is rendered from Redux.
        } finally {
            requestInFlight.current = false;
        }
    };

    const handleBack = () => {
        if (isBusy) return;

        setFormError("");

        if (currentStep === 3) {
            setTimelineSteps([]);
            dispatch(clearBlueprintPreview());
            setCurrentStep(2);
            return;
        }

        dispatch(clearCreateProductState());
        setIsProductPanelOpen(false);
        setCurrentStep(1);
    };

    const renderProductPanel = () => {
        const listStatus =
            productsStatus === "idle" ? "loading" : productsStatus;

        return (
            <aside className="app-panel-slide-left absolute inset-0 z-20 flex h-full min-h-0 w-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl sm:rounded-2xl lg:static lg:z-auto lg:w-80 lg:shrink-0">
                <header className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 bg-[#FAFAFF] px-3 py-3 sm:px-4">
                    <div className="min-w-0">
                        <h3 className="text-base font-semibold text-slate-900">
                            Choose Product
                        </h3>
                        <p className="mt-1 text-sm text-slate-500">
                            Select an existing product for this campaign.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setIsProductPanelOpen(false)}
                        disabled={isBusy}
                        aria-label="Close product list"
                        title="Close"
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-slate-500 transition hover:bg-indigo-50 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <X size={18} aria-hidden="true" />
                    </button>
                </header>

                <div className="product-scrollbar min-h-0 flex-1 overflow-y-auto">
                    <ProductSelectableList
                        products={productList}
                        status={listStatus}
                        error={productsError}
                        selectedProductId={selectedFlowProductId}
                        onRetry={handleRetryProducts}
                        onSelect={handleSelectExistingProduct}
                        showDate={false}
                        showDescription={false}
                        showChevron={false}
                        showSelectedClear
                        onClearSelected={handleClearProduct}
                        emphasizeSelected
                        variant="rows"
                        emptyDescription="Create a product to continue."
                    />
                </div>

                <footer className="shrink-0 border-t border-slate-200 bg-[#FAFAFF] p-2.5 sm:p-3">
                    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Selected Product
                        </p>
                        <div className="mt-1 flex min-w-0 items-center gap-2">
                            <p
                                className={`min-w-0 flex-1 truncate text-sm font-semibold ${
                                    hasSelectedProductForFlow
                                        ? "text-green-700"
                                        : "text-red-700"
                                }`}
                                title={selectedProduct?.name || ""}
                            >
                                {hasSelectedProductForFlow
                                    ? selectedProduct?.name
                                    : "No product selected"}
                            </p>
                            {hasSelectedProductForFlow && (
                                <button
                                    type="button"
                                    onClick={handleClearProduct}
                                    disabled={isBusy}
                                    aria-label="Clear selected product"
                                    title="Clear selected product"
                                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-red-100 bg-red-50 text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    <X size={14} aria-hidden="true" />
                                </button>
                            )}
                        </div>
                    </div>
                </footer>
            </aside>
        );
    };

    const hasBlueprintIncompleteApproveError =
        currentStep === 3 && isBlueprintIncompleteError(approveError);
    const blueprintIncompleteStepNumber = Number(
        approveError?.step_number,
    );
    const blueprintIncompleteStepLabel = Number.isFinite(
        blueprintIncompleteStepNumber,
    )
        ? blueprintIncompleteStepNumber
        : "--";

    const currentError =
        formError ||
        (currentStep === 1 && getModalErrorMessage(createProductError)) ||
        (currentStep === 2 && getModalErrorMessage(previewError)) ||
        (currentStep === 3 &&
            (getModalErrorMessage(previewError) ||
                (!hasBlueprintIncompleteApproveError &&
                    getModalErrorMessage(approveError))));

    useEffect(() => {
        if (currentStep !== 3) return;
        if (!currentError && !hasBlueprintIncompleteApproveError) return;

        const frameId = window.requestAnimationFrame(() => {
            modalBodyRef.current?.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        });

        return () => window.cancelAnimationFrame(frameId);
    }, [currentError, currentStep, hasBlueprintIncompleteApproveError]);

    return (
        <>
            {isOpen && (
                <div className="app-fade-in fixed inset-0 z-[60] flex items-center justify-center overflow-hidden bg-black/40 p-2 backdrop-blur-sm sm:p-4">
                    <button
                        type="button"
                        aria-label="Close campaign modal"
                        className="absolute inset-0 cursor-default"
                        onClick={handleClose}
                        disabled={isBusy}
                    />

                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="campaign-blueprint-title"
                        className={`app-modal-enter relative z-10 flex w-full min-w-0 ${
                            shouldShowProductPanel
                                ? "h-[calc(100dvh-1rem)] max-h-[calc(100dvh-1rem)] max-w-5xl items-stretch overflow-hidden lg:h-[85vh] lg:max-h-[85vh] lg:flex-row lg:gap-4"
                                : "h-[calc(100dvh-1rem)] max-h-[calc(100dvh-1rem)] max-w-2xl sm:h-auto sm:max-h-[calc(100vh-2rem)]"
                        }`}
                    >
                        {shouldShowProductPanel && renderProductPanel()}

                        <section
                            className={`relative flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl sm:rounded-2xl lg:max-w-2xl ${
                                shouldShowProductPanel
                                    ? "h-full"
                                    : "h-full sm:h-auto sm:max-h-[calc(100vh-2rem)]"
                            }`}
                        >
                        <header className="flex shrink-0 items-start justify-between border-b border-slate-200 bg-[#FAFAFF] px-4 py-3 sm:px-6 sm:py-4">
                            <div>
                                <p className="text-sm font-semibold text-indigo-600">
                                    Step {displayStep} of {displayTotalSteps}
                                </p>
                                <h2
                                    id="campaign-blueprint-title"
                                    className="mt-1 text-lg font-bold text-slate-900 sm:text-xl"
                                >
                                    {currentStep === 1 &&
                                        "Choose or Create Product"}
                                    {currentStep === 2 && "Design Campaign"}
                                    {currentStep === 3 && "Preview Timeline"}
                                </h2>
                                {currentStep === 1 && (
                                    <p className="mt-1 text-sm text-slate-500">
                                        choose or create product before creating an campaign
                                    </p>
                                )}
                                {currentStep === 2 && (
                                    <p className="mt-1 text-sm text-slate-500">
                                        You are designing a new campaign
                                    </p>
                                )}
                                {currentStep === 3 && (
                                    <p className="mt-1 text-sm text-slate-500">
                                        Preview or edit timeline before launching campaign
                                    </p>
                                )}
                            </div>

                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={isBusy}
                                aria-label="Close"
                                title="Close"
                                className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 transition hover:bg-indigo-50 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <X size={20} aria-hidden="true" />
                            </button>
                        </header>

                        <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-2.5 sm:px-6 sm:py-3">
                            <div
                                className={`grid gap-2 ${
                                    displayTotalSteps === 2
                                        ? "grid-cols-2"
                                        : "grid-cols-3"
                                }`}
                            >
                                {Array.from(
                                    { length: displayTotalSteps },
                                    (_, index) => index + 1,
                                ).map((step) => (
                                    <div
                                        key={step}
                                        className={`h-1.5 rounded-full transition-colors duration-300 ${
                                            step <= displayStep
                                                ? "bg-indigo-600"
                                                : "bg-slate-200"
                                        }`}
                                    />
                                ))}
                            </div>
                        </div>

                        <div
                            ref={modalBodyRef}
                            key={currentStep}
                            className="app-step-enter min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5"
                        >
                            {currentError && (
                                <div
                                    role="alert"
                                    className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                                >
                                    {currentError}
                                </div>
                            )}

                            {hasBlueprintIncompleteApproveError && (
                                <div
                                    role="alert"
                                    className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900"
                                >
                                    <div className="flex gap-3">
                                        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                                            <AlertTriangle
                                                size={18}
                                                aria-hidden="true"
                                            />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <h3 className="font-semibold text-amber-950">
                                                Blueprint incomplete
                                            </h3>
                                            <p className="mt-1 leading-6">
                                                The AI draft for step{" "}
                                                {blueprintIncompleteStepLabel}{" "}
                                                needs to be regenerated before
                                                this campaign can be launched.
                                            </p>
                                            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                                                <button
                                                    type="button"
                                                    onClick={
                                                        handleRegenerateBlueprintRecovery
                                                    }
                                                    disabled={isBusy}
                                                    className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
                                                >
                                                    Regenerate blueprint
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={
                                                        handleEditBlueprintManually
                                                    }
                                                    disabled={isBusy}
                                                    className="rounded-xl border border-amber-200 bg-white px-4 py-2 text-sm font-semibold text-amber-800 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
                                                >
                                                    Edit manually
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {currentStep === 1 && (
                                <form
                                    id="create-product-form"
                                    className="space-y-3 sm:space-y-4"
                                    onSubmit={handleCreateProduct}
                                >
                                    {hasSelectedProductForFlow && (
                                        <div className="inline-flex rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-100">
                                            {selectedProductBadgeText}
                                        </div>
                                    )}

                                    <div>
                                        <label
                                            htmlFor="campaign-product-name"
                                            className="mb-1.5 block text-sm font-semibold text-slate-700"
                                        >
                                            Product Name
                                        </label>
                                        <input
                                            id="campaign-product-name"
                                            type="text"
                                            value={productName}
                                            onChange={(event) => {
                                                setProductName(
                                                    event.target.value,
                                                );
                                                setFormError("");
                                            }}
                                            readOnly={
                                                hasSelectedProductForFlow
                                            }
                                            disabled={isBusy}
                                            className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:bg-slate-100 ${
                                                hasSelectedProductForFlow
                                                    ? "border-slate-200 bg-slate-50 text-slate-800"
                                                    : "border-slate-200 bg-white text-slate-900"
                                            }`}
                                        />
                                    </div>

                                    <div>
                                        <label
                                            htmlFor="campaign-product-description"
                                            className="mb-1.5 block text-sm font-semibold text-slate-700"
                                        >
                                            Product Description
                                        </label>
                                        <textarea
                                            id="campaign-product-description"
                                            rows={4}
                                            value={productDescription}
                                            onChange={(event) => {
                                                setProductDescription(
                                                    event.target.value,
                                                );
                                                setFormError("");
                                            }}
                                            readOnly={
                                                hasSelectedProductForFlow
                                            }
                                            disabled={isBusy}
                                            className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:bg-slate-100 ${
                                                hasSelectedProductForFlow
                                                    ? "resize-none border-slate-200 bg-slate-50 text-slate-800"
                                                    : "resize-y border-slate-200 bg-white text-slate-900"
                                            }`}
                                        />
                                    </div>
                                </form>
                            )}

                            {currentStep === 2 && (
                                <form
                                    id="design-campaign-form"
                                    className="space-y-3 sm:space-y-4"
                                    onSubmit={handleDesignCampaign}
                                >
                                    <div className="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3">
                                        <p className="text-xs font-semibold uppercase text-indigo-600">
                                            Product
                                        </p>
                                        <p className="mt-1 font-semibold text-slate-900">
                                            {selectedProduct?.name ||
                                                "Product unavailable"}
                                        </p>
                                    </div>

                                    <div>
                                        <label
                                            htmlFor="campaign-name"
                                            className="mb-1.5 block text-sm font-semibold text-slate-700"
                                        >
                                            Campaign Name
                                        </label>
                                        <input
                                            id="campaign-name"
                                            type="text"
                                            value={campaignName}
                                            onChange={(event) => {
                                                setCampaignName(
                                                    event.target.value,
                                                );
                                                setFormError("");
                                            }}
                                            disabled={isBusy}
                                            placeholder="Example: CTO Outreach - Pune SaaS Companies"
                                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:bg-slate-100"
                                        />
                                    </div>

                                    <div>
                                        <label
                                            htmlFor="campaign-objective"
                                            className="mb-1.5 block text-sm font-semibold text-slate-700"
                                        >
                                            Campaign Objective
                                        </label>
                                        <textarea
                                            id="campaign-objective"
                                            rows={3}
                                            value={campaignObjective}
                                            onChange={(event) => {
                                                setCampaignObjective(
                                                    event.target.value,
                                                );
                                                setFormError("");
                                            }}
                                            disabled={isBusy}
                                            className="w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:bg-slate-100"
                                        />
                                    </div>

                                    <div>
                                        <label
                                            htmlFor="campaign-target-audience"
                                            className="mb-1.5 block text-sm font-semibold text-slate-700"
                                        >
                                            Target Audience
                                        </label>
                                        <textarea
                                            id="campaign-target-audience"
                                            rows={3}
                                            value={targetAudience}
                                            onChange={(event) => {
                                                setTargetAudience(
                                                    event.target.value,
                                                );
                                                setFormError("");
                                            }}
                                            disabled={isBusy}
                                            className="w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:bg-slate-100"
                                        />
                                    </div>

                                    <div>
                                        <label
                                            htmlFor="campaign-product-value-proposition"
                                            className="mb-1.5 block text-sm font-semibold text-slate-700"
                                        >
                                            Product Value Proposition
                                        </label>
                                        <textarea
                                            id="campaign-product-value-proposition"
                                            rows={3}
                                            value={productValueProposition}
                                            onChange={(event) => {
                                                setProductValueProposition(
                                                    event.target.value,
                                                );
                                                setFormError("");
                                            }}
                                            disabled={isBusy}
                                            className="w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:bg-slate-100"
                                        />
                                    </div>

                                    <div>
                                        <label
                                            htmlFor="campaign-tone-setting"
                                            className="mb-1.5 block text-sm font-semibold text-slate-700"
                                        >
                                            Tone Setting
                                        </label>
                                        <textarea
                                            id="campaign-tone-setting"
                                            rows={2}
                                            value={toneSetting}
                                            onChange={(event) => {
                                                setToneSetting(
                                                    event.target.value,
                                                );
                                                setFormError("");
                                            }}
                                            disabled={isBusy}
                                            className="w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:bg-slate-100"
                                        />
                                    </div>
                                </form>
                            )}

                            {currentStep === 3 && (
                                <div className="space-y-3 sm:space-y-4">
                                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                                        <p className="text-xs font-semibold uppercase text-slate-500">
                                            Campaign
                                        </p>
                                        <p className="mt-1 font-semibold text-slate-900">
                                            {campaignName.trim() ||
                                                "Campaign name unavailable"}
                                        </p>
                                    </div>

                                    <div
                                        className={`rounded-xl border px-4 py-3 text-sm ${
                                            hasReachedCampaignCap
                                                ? "border-amber-200 bg-amber-50 text-amber-800"
                                                : "border-indigo-100 bg-indigo-50/70 text-slate-700"
                                        }`}
                                    >
                                        {isCheckingCampaignCap ? (
                                            <p className="font-medium text-indigo-700">
                                                Checking campaign slots...
                                            </p>
                                        ) : capStatusStatus === "failed" ? (
                                            <p className="font-medium text-amber-700">
                                                Unable to verify campaign limit
                                                right now.
                                                {capStatusError
                                                    ? ` ${capStatusError}`
                                                    : ""}
                                            </p>
                                        ) : capStatus ? (
                                            <>
                                                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                                                    <p className="font-semibold text-slate-900">
                                                        Campaign slots:{" "}
                                                        {formatCapValue(
                                                            capStatus.current_count,
                                                        )}{" "}
                                                        /{" "}
                                                        {formatCapValue(
                                                            capStatus.cap,
                                                        )}{" "}
                                                        used
                                                    </p>
                                                    <p className="text-sm font-medium text-slate-600">
                                                        {formatCapValue(
                                                            capStatus.remaining,
                                                        )}{" "}
                                                        launches remaining
                                                    </p>
                                                </div>
                                                {hasReachedCampaignCap && (
                                                    <p className="mt-2 text-sm font-medium text-amber-800">
                                                        {campaignCapWarning}
                                                    </p>
                                                )}
                                            </>
                                        ) : (
                                            <p className="font-medium text-slate-600">
                                                Campaign slot usage is not
                                                available yet.
                                            </p>
                                        )}
                                    </div>

                                    {timelineSteps.length === 0 ? (
                                        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-800">
                                            No campaign steps were generated.
                                            Go back and design the campaign
                                            again.
                                        </div>
                                    ) : (
                                        timelineSteps.map((step, index) => {
                                            const fallbackSubjectValue =
                                                getStepTextValue(
                                                    step.subject,
                                                );
                                            const fallbackSubject =
                                                fallbackSubjectValue.trim();
                                            const fallbackBodyValue =
                                                getStepTextValue(
                                                    step.html_body,
                                                );
                                            const fallbackBodyText =
                                                getPlainTextFromHtml(
                                                    fallbackBodyValue,
                                                );
                                            const hasFallbackEmailContent =
                                                isEmailStep(step) &&
                                                (Object.prototype.hasOwnProperty.call(
                                                    step,
                                                    "subject",
                                                ) ||
                                                    Object.prototype.hasOwnProperty.call(
                                                        step,
                                                        "html_body",
                                                    ) ||
                                                    fallbackSubject ||
                                                    fallbackBodyValue);

                                            return (
                                                <div
                                                    key={`${step.step_number}-${index}`}
                                                    className="border-l-2 border-indigo-500 pl-3 sm:border-l-4 sm:pl-4"
                                                >
                                                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                                                        <p className="font-semibold text-slate-900">
                                                            Step No:{" "}
                                                            {step.step_number ??
                                                                index + 1}
                                                        </p>
                                                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase text-slate-700">
                                                            {step.channel_type ||
                                                                "Unknown channel"}
                                                        </span>
                                                    </div>

                                                    <div className="grid gap-3 sm:grid-cols-[160px_minmax(0,1fr)]">
                                                        <div>
                                                            <label
                                                                htmlFor={`campaign-step-delay-${index}`}
                                                                className="mb-1.5 block text-sm font-semibold text-slate-700"
                                                            >
                                                                Delay (days)
                                                            </label>
                                                            <input
                                                                id={`campaign-step-delay-${index}`}
                                                                type="number"
                                                                min={
                                                                    index === 0
                                                                        ? "0"
                                                                        : "1"
                                                                }
                                                                step="1"
                                                                value={normalizeDelayDays(
                                                                    step.delay_days_from_previous,
                                                                    index,
                                                                )}
                                                                onChange={(
                                                                    event,
                                                                ) =>
                                                                    handleTimelineChange(
                                                                        index,
                                                                        "delay_days_from_previous",
                                                                        event
                                                                            .target
                                                                            .value,
                                                                    )
                                                                }
                                                                disabled={
                                                                    isBusy
                                                                }
                                                                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:bg-slate-100"
                                                            />
                                                        </div>

                                                        <div>
                                                            <label
                                                                htmlFor={`campaign-step-objective-${index}`}
                                                                className="mb-1.5 block text-sm font-semibold text-slate-700"
                                                            >
                                                                Step Objective
                                                            </label>
                                                            <textarea
                                                                id={`campaign-step-objective-${index}`}
                                                                rows={3}
                                                                value={
                                                                    step.step_objective ||
                                                                    ""
                                                                }
                                                                onChange={(
                                                                    event,
                                                                ) =>
                                                                    handleTimelineChange(
                                                                        index,
                                                                        "step_objective",
                                                                        event
                                                                            .target
                                                                            .value,
                                                                    )
                                                                }
                                                                disabled={
                                                                    isBusy
                                                                }
                                                                className="w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:bg-slate-100"
                                                            />
                                                        </div>

                                                        {hasFallbackEmailContent && (
                                                            <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-3 sm:col-span-2">
                                                                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                                                                    <div>
                                                                        <p className="text-sm font-semibold text-slate-700">
                                                                            Fallback
                                                                            email
                                                                        </p>
                                                                        <p className="mt-1 text-xs font-medium text-amber-700">
                                                                            Used
                                                                            only
                                                                            if
                                                                            personalized
                                                                            email
                                                                            generation
                                                                            fails.
                                                                        </p>
                                                                    </div>
                                                                </div>

                                                                <div className="mt-3 space-y-3">
                                                                    <div>
                                                                        <label
                                                                            htmlFor={`campaign-step-subject-${index}`}
                                                                            className="text-xs font-semibold uppercase text-slate-700"
                                                                        >
                                                                            Subject
                                                                        </label>
                                                                        <input
                                                                            id={`campaign-step-subject-${index}`}
                                                                            type="text"
                                                                            value={
                                                                                fallbackSubjectValue
                                                                            }
                                                                            onChange={(
                                                                                event,
                                                                            ) =>
                                                                                handleTimelineChange(
                                                                                    index,
                                                                                    "subject",
                                                                                    event
                                                                                        .target
                                                                                        .value,
                                                                                )
                                                                            }
                                                                            disabled={
                                                                                isBusy
                                                                            }
                                                                            className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium leading-5 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:bg-slate-100"
                                                                        />
                                                                    </div>

                                                                    <div>
                                                                        <label
                                                                            htmlFor={`campaign-step-html-body-${index}`}
                                                                            className="text-xs font-semibold uppercase text-slate-700"
                                                                        >
                                                                            Email
                                                                            body
                                                                        </label>
                                                                        <textarea
                                                                            id={`campaign-step-html-body-${index}`}
                                                                            rows={6}
                                                                            value={
                                                                                fallbackBodyText
                                                                            }
                                                                            onChange={(
                                                                                event,
                                                                            ) =>
                                                                                handleTimelineChange(
                                                                                    index,
                                                                                    "html_body",
                                                                                    plainTextToSimpleHtml(
                                                                                        event
                                                                                            .target
                                                                                            .value,
                                                                                    ),
                                                                                )
                                                                            }
                                                                            disabled={
                                                                                isBusy
                                                                            }
                                                                            className="mt-1 w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm leading-5 text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:bg-slate-100"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            )}
                        </div>

                        <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-slate-200 bg-[#FAFAFF] px-3 py-3 sm:gap-3 sm:px-6 sm:py-4">
                            {currentStep === 1 && (
                                <div
                                    className={`grid w-full gap-2 sm:flex sm:w-auto sm:gap-3 ${
                                        hasSelectedProductForFlow
                                            ? "grid-cols-2"
                                            : "grid-cols-1"
                                    }`}
                                >
                                    {hasSelectedProductForFlow ? (
                                        <>
                                            <button
                                                type="button"
                                                onClick={
                                                    handleClearProduct
                                                }
                                                disabled={isBusy}
                                                className="min-h-10 min-w-0 w-full rounded-xl border border-indigo-100 bg-indigo-50 px-2 py-2.5 text-xs font-semibold leading-tight text-indigo-700 transition hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-5 sm:text-sm"
                                            >
                                                Clear Product
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleGoToNextStep}
                                                disabled={isBusy}
                                                className="min-h-10 min-w-0 w-full rounded-xl bg-indigo-600 px-2 py-2.5 text-xs font-semibold leading-tight text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300 sm:w-auto sm:px-5 sm:text-sm"
                                            >
                                                {isCheckingStatusGuard
                                                    ? "Checking..."
                                                    : "Next"}
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            type="submit"
                                            form="create-product-form"
                                            disabled={
                                                isBusy || !canCreateNewProduct
                                            }
                                            className="min-h-10 min-w-0 w-full rounded-xl bg-indigo-600 px-2 py-2.5 text-xs font-semibold leading-tight text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300 sm:w-auto sm:px-5 sm:text-sm"
                                        >
                                            {createProductStatus === "loading"
                                                ? "Creating Product..."
                                                : isCheckingStatusGuard
                                                ? "Checking..."
                                                : "Create Product"}
                                        </button>
                                    )}
                                </div>
                            )}

                            {currentStep === 2 && (
                                <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:gap-3">
                                    <button
                                        type="button"
                                        onClick={handleBack}
                                        disabled={isBusy}
                                        className="min-h-10 min-w-0 w-full rounded-xl border border-indigo-100 bg-indigo-50 px-3 py-2.5 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-4"
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        form="design-campaign-form"
                                        disabled={isBusy || !canDesignCampaign}
                                        className="min-h-10 min-w-0 w-full rounded-xl bg-indigo-600 px-3 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300 sm:w-auto sm:px-5"
                                    >
                                        {previewStatus === "loading"
                                            ? "Designing Campaign..."
                                            : "Design Campaign"}
                                    </button>
                                </div>
                            )}

                            {currentStep === 3 && (
                                <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:gap-3">
                                    <button
                                        type="button"
                                        onClick={handleBack}
                                        disabled={isBusy}
                                        className="min-h-10 min-w-0 w-full rounded-xl border border-indigo-100 bg-indigo-50 px-3 py-2.5 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-4"
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleApproveCampaign}
                                        disabled={
                                            isBusy ||
                                            isCheckingCampaignCap ||
                                            hasReachedCampaignCap ||
                                            timelineSteps.length === 0 ||
                                            !hasValidTimelineStepText
                                        }
                                        className="min-h-10 min-w-0 w-full rounded-xl bg-emerald-600 px-3 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300 sm:w-auto sm:px-5"
                                    >
                                        {isCheckingCampaignCap
                                            ? "Checking Slots..."
                                            : approveStatus === "loading"
                                            ? "Starting Campaign..."
                                            : "Approve & Start"}
                                    </button>
                                </div>
                            )}
                        </footer>
                        <CampaignDesignLoader
                            isLoading={previewStatus === "loading"}
                        />
                        </section>
                    </div>
                </div>
            )}
        </>
    );
};

export default CampaignBlueprintModal;
